import {
  ActionConfirmationModal,
  Button,
  Form,
  FormSubmitButton,
  MultiselectFormField,
  PhoneNumberFormField,
  TextFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  getPermissionGroupsQueryOptions,
  useCreateUser,
  useDeleteUser,
  useDisableUser,
  useEnableUser,
  useResetUserMfa,
  useUpdateUser,
} from "@/lib/data/react-query";
import { UserSchema } from "@/lib/data/schemas/firm/FirmSchema";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type SettingsUser } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { type z } from "zod";

const FORM_ID = "invite-or-edit-user-form";

interface InviteOrEditUserModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

type ConditionalProps =
  | {
      isReadOnly?: boolean;
      editing: true;
      activeUser: SettingsUser;
      onUserUpdated: (user: SettingsUser) => void;
    }
  | {
      isReadOnly?: never;
      editing: false;
      activeUser?: never;
      onUserUpdated?: never;
    };

const formSchema = UserSchema.pick({
  firstName: true,
  middleName: true,
  lastName: true,
  email: true,
  phoneNumber: true,
  permissionGroupIds: true,
  barNumber: true,
});

const createFormSchema = formSchema.extend({
  permissionGroupIds: formSchema.shape.permissionGroupIds.min(
    1,
    "At least one permission group is required",
  ),
});

export function InviteOrEditUserModal({
  open,
  setOpen,
  activeUser,
  editing,
  isReadOnly,
  onUserUpdated,
}: InviteOrEditUserModalProps & ConditionalProps) {
  const { currentUser, hasActionPermission } = useUser();
  const isEditingCurrentUser = editing && activeUser.id === currentUser.id;
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(editing ? formSchema : createFormSchema),
    values: editing
      ? {
          firstName: activeUser.firstName,
          middleName: activeUser.middleName,
          lastName: activeUser.lastName,
          email: activeUser.email,
          phoneNumber: activeUser.phoneNumber,
          permissionGroupIds: activeUser.permissionGroups.map(
            (group) => group.id,
          ),
          barNumber: activeUser.barNumber,
        }
      : {
          firstName: "",
          middleName: "",
          lastName: "",
          email: "",
          phoneNumber: "",
          permissionGroupIds: [],
          barNumber: "",
        },
    mode: "onSubmit",
  });

  const {
    data: permissionGroupsData,
    isLoading: isPermissionGroupsLoading,
    isError: isPermissionGroupsError,
  } = useQuery(getPermissionGroupsQueryOptions());

  const permissionGroupOptions = (permissionGroupsData?.data ?? []).map(
    (group) => ({ label: group.label, value: group.id }),
  );

  const createUser = useCreateUser();
  const editUser = useUpdateUser();
  const disableUser = useDisableUser();
  const enableUser = useEnableUser();
  const deleteUser = useDeleteUser();
  const resetUserMfa = useResetUserMfa();
  const [confirmToggleOpen, setConfirmToggleOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmResetMfaOpen, setConfirmResetMfaOpen] = useState(false);

  const isFormLoading =
    createUser.isPending ||
    editUser.isPending ||
    disableUser.isPending ||
    enableUser.isPending ||
    deleteUser.isPending ||
    resetUserMfa.isPending;
  const canUpdateUsers = hasActionPermission(
    ACTION_PERMISSION_KEYS.user,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDeleteUsers = hasActionPermission(
    ACTION_PERMISSION_KEYS.user,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canResetMfa = hasActionPermission(
    ACTION_PERMISSION_KEYS.user,
    ACTION_PERMISSION_VALUES.update,
  );

  const modalTitle = useMemo(
    () => (isReadOnly ? "View user" : editing ? "Update user" : "Invite user"),
    [isReadOnly, editing],
  );

  const modalDescription = useMemo(
    () =>
      isReadOnly
        ? "View the user details below"
        : editing
          ? "Update the user details below"
          : "Invite a new user to your firm",
    [isReadOnly, editing],
  );

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (editing) {
      const response = await editUser.mutateAsync({
        id: activeUser.id,
        dateModified: activeUser.dateModified,
        firstName: data.firstName,
        middleName: data.middleName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber,
        permissionGroupIds: data.permissionGroupIds,
        barNumber: data.barNumber,
      });
      onUserUpdated(response.data);
    } else {
      await createUser.mutateAsync({
        ...data,
      });
    }
    setOpen(false);
    form.reset();
  };

  const handleToggleUserStatus = async () => {
    if (!editing) return;
    const response = activeUser.enabled
      ? await disableUser.mutateAsync(activeUser.id)
      : await enableUser.mutateAsync(activeUser.id);
    onUserUpdated(response.data);
  };

  const handleDeleteUser = async () => {
    if (!editing || activeUser.owner) return;
    await deleteUser.mutateAsync(activeUser.id);
    setOpen(false);
  };

  const handleResetMfa = async () => {
    if (!editing) return;
    await resetUserMfa.mutateAsync(activeUser.id);
  };

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const adminActions =
    editing &&
    !isReadOnly &&
    currentUser.id !== activeUser.id &&
    (canUpdateUsers || canDeleteUsers || canResetMfa) ? (
      <div className="flex flex-wrap items-center gap-2">
        {activeUser.enabled && canResetMfa && (
          <Button
            type="button"
            variant="outline"
            disabled={isFormLoading}
            onClick={() => {
              setConfirmResetMfaOpen(true);
            }}
          >
            Reset MFA
          </Button>
        )}
        {canUpdateUsers && (
          <Button
            type="button"
            variant={activeUser.enabled ? "destructive" : "default"}
            disabled={isFormLoading}
            onClick={() => {
              setConfirmToggleOpen(true);
            }}
          >
            {activeUser.enabled ? "Disable user" : "Enable user"}
          </Button>
        )}

        {!activeUser.owner && !activeUser.enabled && canDeleteUsers && (
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              setConfirmDeleteOpen(true);
            }}
            disabled={isFormLoading}
          >
            Delete user
          </Button>
        )}
      </div>
    ) : null;

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          isDirty={form.formState.isDirty}
          title={modalTitle}
          description={modalDescription}
          loading={isFormLoading}
          size="2xl"
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              {adminActions}
              <div className="flex grow justify-end space-x-2">
                <FormCancelButton
                  label={isReadOnly ? "Close" : "Cancel"}
                  setOpen={setOpen}
                  isDirty={form.formState.isDirty}
                />
                {!isReadOnly && (
                  <FormSubmitButton
                    submitFormId={FORM_ID}
                    loading={isFormLoading}
                  />
                )}
              </div>
            </div>
          }
        >
          <form
            id={FORM_ID}
            className="flex min-h-0 grow flex-col overflow-hidden"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="overflow-y-auto">
              <fieldset
                disabled={isReadOnly}
                className="my-4 grid grid-cols-1 gap-4 p-6 sm:grid-cols-2"
              >
                <TextFormField
                  register={form.register("firstName")}
                  label="First name*"
                />
                <TextFormField
                  register={form.register("middleName")}
                  label="Middle name"
                />
                <TextFormField
                  register={form.register("lastName")}
                  label="Last name*"
                />
                <TextFormField
                  register={form.register("email")}
                  disabled={editing}
                  label="Email*"
                />
                <PhoneNumberFormField
                  register={form.register("phoneNumber")}
                  label="Phone number*"
                />
                <MultiselectFormField
                  label="Permission groups*"
                  register={form.register("permissionGroupIds")}
                  disabled={isEditingCurrentUser}
                  options={permissionGroupOptions}
                  maxCount={undefined}
                  placeholder={
                    isPermissionGroupsLoading
                      ? "Loading permission groups..."
                      : isPermissionGroupsError
                        ? "Unable to load permission groups"
                        : "Select permission groups"
                  }
                />
                <TextFormField
                  register={form.register("barNumber")}
                  label="Bar number"
                />
              </fieldset>
            </div>
          </form>
        </AppModal>
      </Form>
      <ActionConfirmationModal
        open={confirmResetMfaOpen}
        setOpen={setConfirmResetMfaOpen}
        title="Reset MFA for this user?"
        description="They will be prompted to set up MFA on their next sign in."
        buttonText="Reset MFA"
        afterActionFn={handleResetMfa}
      />
      <ActionConfirmationModal
        open={confirmToggleOpen}
        setOpen={setConfirmToggleOpen}
        title={activeUser?.enabled ? "Disable this user?" : "Enable this user?"}
        description={
          activeUser?.enabled
            ? "They won’t be able to sign in until re-enabled."
            : "They will be able to sign in again."
        }
        buttonText={activeUser?.enabled ? "Disable" : "Enable"}
        afterActionFn={handleToggleUserStatus}
      />
      <ActionConfirmationModal
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        title="Delete this user?"
        description="This action is permanent and cannot be undone."
        buttonText="Delete"
        afterActionFn={handleDeleteUser}
      />
    </>
  );
}
